<template>
  <div class="admin-login">
    <div class="login-container">
      <div class="login-form">
        <div class="login-header">
          <div class="logo">
            <el-icon class="logo-icon"><Management /></el-icon>
            <h2>管理后台</h2>
          </div>
          <p>请输入管理员账号和密码</p>
        </div>
        
        <el-form 
          :model="loginForm" 
          :rules="loginRules" 
          ref="loginFormRef"
          @keyup.enter="submitForm"
        >
          <el-form-item prop="username">
            <el-input 
              v-model="loginForm.username" 
              placeholder="用户名"
              :prefix-icon="User"
              size="large"
            />
          </el-form-item>
          
          <el-form-item prop="password">
            <el-input 
              v-model="loginForm.password" 
              type="password"
              placeholder="密码"
              :prefix-icon="Lock"
              size="large"
              show-password
            />
          </el-form-item>
          
          <el-checkbox v-model="rememberMe" style="margin-bottom: 20px;">记住我</el-checkbox>
          
          <el-form-item>
            <el-button 
              type="primary" 
              @click="submitForm" 
              :loading="loading"
              size="large"
              style="width: 100%;"
            >
              <span v-if="!loading">登录</span>
              <span v-else>登录中...</span>
            </el-button>
          </el-form-item>
        </el-form>
        
        <div class="links">
          <el-link @click="goToHome" type="primary" :underline="false">返回首页</el-link>
          <el-link href="#" type="primary" :underline="false">忘记密码？</el-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { User, Lock, Management } from '@element-plus/icons-vue';
import { adminLogin } from '@/admin/api/auth';

const router = useRouter();

const loginFormRef = ref();
const loading = ref(false);
const rememberMe = ref(false);

const loginForm = reactive({
  username: '',
  password: ''
});

const loginRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ]
};

const submitForm = async () => {
  try {
    await loginFormRef.value.validate();
    loading.value = true;
    
    const response = await adminLogin(loginForm.username, loginForm.password);
    
    if (response.code === 1) {
      // 存储token和用户信息
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUserInfo', JSON.stringify(response.data.userInfo));
      localStorage.setItem('adminMenus', JSON.stringify(response.data.menus));
      
      ElMessage.success('登录成功');
      // 跳转到管理后台首页
      router.push('/admin/dashboard');
    } else {
      ElMessage.error(response.msg || '登录失败');
    }
  } catch (error) {
    console.error('Login error:', error);
    ElMessage.error('登录失败，请检查用户名和密码');
  } finally {
    loading.value = false;
  }
};

const goToHome = () => {
  router.push('/');
};
</script>

<style scoped>
.admin-login {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../../../background/background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.login-container {
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 1;
  /* 添加玻璃流动动画效果 */
  animation: glassFlow 15s ease-in-out infinite alternate;
}

/* 新增玻璃流动动画 */
@keyframes glassFlow {
  0% {
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  50% {
    background: rgba(255, 255, 255, 0.35);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.25);
  }
  100% {
    background: rgba(255, 255, 255, 0.28);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
}

.login-form {
  padding: 40px;
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.logo-icon {
  font-size: 32px;
  color: #409eff;
}

.login-header h2 {
  margin: 0;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.login-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.login-form .el-form-item {
  margin-bottom: 24px;
}

.login-form .el-input__wrapper {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s;
  background: rgba(255, 255, 255, 0.7);
}

.login-form .el-input__wrapper:hover {
  border-color: #409eff;
}

.login-form .el-input__wrapper.is-focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.3);
}

.login-footer {
  display: flex;
  justify-content: space-between;
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

@media (max-width: 768px) {
  .login-form {
    padding: 30px 20px;
  }
  
  .login-footer {
    flex-direction: column;
    gap: 15px;
  }
}
</style>
